/**
 * @copyright
 * Copyright (c) 2022 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { StatisticsGroupBy, StatisticsTab } from "~/features/statistics/constants";
import { StatisticsTabConfig } from "~/features/statistics/types";

const statisticsTabConfigMap: { [key in StatisticsTab]: StatisticsTabConfig } = {
  [StatisticsTab.TOTAL_STATS]: {
    columnKeys: [
      "siteDomain",
      "totalClicks",
      "uniqueLogins",
      "nonPremiumJoins",
      "premiumJoins",
      "newPayingUsers",
      "usersAboveThreshold",
      "successfulThrustsAmount",
      "totalChargedBack",
      "totalChargedBackCount",
      "totalRefunded",
      "netRevenue",
      "payoutProgramType",
      "netPayout",
    ],
    activeColumns: {
      siteDomain: true, // Domain
      totalClicks: true, // Clicks
      uniqueLogins: false, // Unique logins
      nonPremiumJoins: false, // Non-premium joins
      premiumJoins: true, // Premium joins
      newPayingUsers: true, // New paying users
      usersAboveThreshold: false, // High spenders
      successfulThrustsAmount: true, // Gross revenue
      totalChargedBack: true, // Chargeback
      totalChargedBackCount: false, // Chargebacks count
      totalRefunded: true, // Refund
      netRevenue: true, // Net revenue
      payoutProgramType: true, // Program
      netPayout: true, // Estimated payout
    },
    columnGroup: {},
    excludedSites: true,
    deviceFilter: false,
    trackingId: true,
    enabledGroups: [
      StatisticsGroupBy.DATE,
      StatisticsGroupBy.TRACKING_ID,
      StatisticsGroupBy.REFERRER_ID,
      StatisticsGroupBy.PROCESSORID,
    ],
  },
  [StatisticsTab.PLATFORM_STATS]: {
    columnKeys: [
      "device",
      "totalClicks",
      "nonPremiumJoins",
      "premiumJoins",
      "newPayingUsers",
      "usersAboveThreshold",
      "successfulThrustsAmount",
      "totalChargedBack",
      "totalChargedBackCount",
      "totalRefunded",
      "netRevenue",
    ],
    activeColumns: {
      device: true, // Platform
      totalClicks: true, // Clicks
      nonPremiumJoins: false, // Non-premium joins
      premiumJoins: true, // Premium joins
      newPayingUsers: true, // Nes paying users
      usersAboveThreshold: false, // High spenders
      successfulThrustsAmount: true, // Gross revenue
      totalChargedBack: true, // Chargeback
      totalChargedBackCount: false, // Chargebacks count
      totalRefunded: true, // Refund
      netRevenue: true, // Net revenue
    },
    columnGroup: {},
    excludedSites: true,
    deviceFilter: false,
    trackingId: true,
    enabledGroups: [StatisticsGroupBy.DATE, StatisticsGroupBy.TRACKING_ID],
  },
  [StatisticsTab.USER_VALUE]: {
    columnKeys: [
      "siteDomain",
      "premiumJoins",
      "spenderCountTotal",
      "spenderCountPercentageTotal",
      "successfulThrustsAmountTotal",
      "spenderCountFirstDay",
      "spenderCountPercentageFirstDay",
      "successfulThrustsAmountFirstDay",
      "averageAmountPerSpenderFirstDay",
      "spenderCountFirstWeek",
      "spenderCountPercentageFirstWeek",
      "successfulThrustsAmountFirstWeek",
      "averageAmountPerSpenderFirstWeek",
      "spenderCountFirst30Days",
      "spenderCountPercentageFirst30Days",
      "successfulThrustsAmountFirst30Days",
      "averageAmountPerSpenderFirst30Days",
      "spenderCountOver30Days",
      "spenderCountPercentageOver30Days",
      "successfulThrustsAmountOver30Days",
    ],
    activeColumns: {
      siteDomain: true,
      premiumJoins: true,
      spenderTotals: true,
      firstDay: true,
      firstWeek: true,
      first30Days: true,
      over30Days: true,
    },
    excludedSites: true,
    deviceFilter: true,
    trackingId: true,
    enabledGroups: [
      StatisticsGroupBy.DATE,
      StatisticsGroupBy.TRACKING_ID,
      StatisticsGroupBy.COUNTRY,
      StatisticsGroupBy.PLATFORM,
    ],
    columnGroup: {
      siteDomain: ["siteDomain"],
      premiumJoins: ["premiumJoins"],
      spenderTotals: ["spenderCountTotal", "spenderCountPercentageTotal", "successfulThrustsAmountTotal"],
      firstDay: [
        "spenderCountFirstDay",
        "spenderCountPercentageFirstDay",
        "successfulThrustsAmountFirstDay",
        "averageAmountPerSpenderFirstDay",
      ],
      firstWeek: [
        "spenderCountFirstWeek",
        "spenderCountPercentageFirstWeek",
        "successfulThrustsAmountFirstWeek",
        "averageAmountPerSpenderFirstWeek",
      ],
      first30Days: [
        "spenderCountFirst30Days",
        "spenderCountPercentageFirst30Days",
        "successfulThrustsAmountFirst30Days",
        "averageAmountPerSpenderFirst30Days",
      ],
      over30Days: ["spenderCountOver30Days", "spenderCountPercentageOver30Days", "successfulThrustsAmountOver30Days"],
    },
  },
  [StatisticsTab.GEO]: {
    columnKeys: [
      "country",
      "totalClicks",
      "nonPremiumJoins",
      "premiumJoins",
      "newPayingUsers",
      "usersAboveThreshold",
      "successfulThrustsAmount",
      "totalChargedBack",
      "totalChargedBackCount",
      "totalRefunded",
      "netRevenue",
    ],
    activeColumns: {
      country: true, // Country
      totalClicks: true, // Clicks
      nonPremiumJoins: false, // Non-premium joins
      premiumJoins: true, // Premium joins
      newPayingUsers: true, // New paying users
      usersAboveThreshold: false, // High spenders
      successfulThrustsAmount: true, // Gross revenue
      totalChargedBack: true, // Chargeback
      totalChargedBackCount: false, // Chargebacks count
      totalRefunded: true, // Refund
      netRevenue: true, // Net revenue
    },
    columnGroup: {},
    excludedSites: true,
    deviceFilter: false,
    trackingId: true,
    enabledGroups: [StatisticsGroupBy.DATE, StatisticsGroupBy.TRACKING_ID],
  },
  [StatisticsTab.UPSELL_STATS]: {
    columnKeys: [
      "siteDomain",
      "salesAmount",
      "totalAdjustment",
      "salesCount",
      "chargebackCount",
      "cancelCount",
      "netSales",
      "resellerPayout",
    ],
    activeColumns: {
      siteDomain: true, // Domain
      salesAmount: true, // Gross revenue
      totalAdjustment: true, // Total adjustment
      salesCount: true, // Subscribers
      chargebackCount: true, // Chargeback count
      cancelCount: true, // Cancels count
      netSales: true, // Net revenue
      resellerPayout: true, // Estimated payout
    },
    columnGroup: {},
    excludedSites: true,
    deviceFilter: false,
    trackingId: true,
    enabledGroups: [StatisticsGroupBy.DATE],
  },
  [StatisticsTab.PAST_PAYOUTS]: {
    columnKeys: ["paymentDate", "payMethod", "paymentAmount", "payPeriod"],
    activeColumns: {
      paymentDate: true,
      payMethod: true,
      paymentAmount: true,
      payPeriod: true,
    },
    columnGroup: {},
    excludedSites: false,
    deviceFilter: false,
    trackingId: false,
    enabledGroups: [StatisticsGroupBy.DATE],
  },
  [StatisticsTab.USER_SPENDING]: {
    columnKeys: ["userId", "country", "platform", "activity", "model", "amount", "referrerKey", "trackingId"],
    activeColumns: {
      userId: true,
      country: true,
      platform: true,
      activity: true,
      model: true,
      amount: true,
      referrerKey: true,
      trackingId: true,
    },
    columnGroup: {},
    excludedSites: true,
    deviceFilter: false,
    trackingId: false,
    enabledGroups: [StatisticsGroupBy.DATE],
  },
  [StatisticsTab.VIDEO_SALES]: {
    columnKeys: [
      "domain",
      "videoRevSharePercentage",
      "videoSalesCount",
      "totalVideoSales",
      "estimatedVideoSalesRevenue",
    ],
    activeColumns: {
      domain: true,
      videoRevSharePercentage: true,
      videoSalesCount: true,
      totalVideoSales: true,
      estimatedVideoSalesRevenue: true,
    },
    columnGroup: {},
    excludedSites: true,
    deviceFilter: false,
    trackingId: true,
    enabledGroups: [StatisticsGroupBy.DATE],
  },
};

export default statisticsTabConfigMap;
